// src/pages/RepartitionCompetiteursTapisPage/index.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import { getCompetitionById } from "../../services/competitionsServices";
import { getCompetitionTapisDistributions } from "../../services/distributionsServices";
import { countDistributionCombats } from "../../services/combatsServices";

import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";
import Breadcrumb from "../../components/Breadcrumb";

import RepartitionPouleDeux from "../../components/FormulesRepartitions/RepartitionPouleDeux";
import RepartitionPouleTrois from "../../components/FormulesRepartitions/RepartitionPouleTrois";
import RepartitionPouleQuatre from "../../components/FormulesRepartitions/RepartitionPouleQuatre";
import RepartitionPouleCinq from "../../components/FormulesRepartitions/RepartitionPouleCinq";
import RepartitionPouleSix from "../../components/FormulesRepartitions/RepartitionPouleSix";
import RepartitionTableauSansRepechage from "../../components/FormulesRepartitions/RepartitionTableauSansRepechage";
import RepartitionTableauSimpleRepechage from "../../components/FormulesRepartitions/RepartitionTableauSimpleRepechage";
import RepartitionTableauSystematiqueRepechage from "../../components/FormulesRepartitions/RepartitionTableauSystematiqueRepechage";

import socket from "../../components/ScoreboardAdmin/socketClient";
import { FaPause, FaPlay } from "react-icons/fa";

import {
  FaSyncAlt,
  FaClock,
  FaCheckCircle,
  FaExternalLinkAlt,
} from "react-icons/fa";

/* ---------------------------------------------
   Utils
--------------------------------------------- */

const safeArr = (val) => {
  if (Array.isArray(val)) return val;
  try {
    const p = JSON.parse(val || "[]");
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

const isPoule = (formule = "") => /^poule_/i.test(String(formule));
const isBracket = (formule = "") => /^tableau_/i.test(String(formule));

const formatFormule = (formule) => {
  switch (formule) {
    case "poule_2":
      return "Poule de 2";
    case "poule_3":
      return "Poule de 3";
    case "poule_4":
      return "Poule de 4";
    case "poule_5":
      return "Poule de 5";
    case "poule_6":
      return "Poule de 6";
    case "tableau_base_8":
      return "Tableau sans repêchage (Base 8)";
    case "tableau_base_16":
      return "Tableau sans repêchage (Base 16)";
    case "tableau_base_32":
      return "Tableau sans repêchage (Base 32)";
    case "tableau_base_64":
      return "Tableau sans repêchage (Base 64)";
    default:
      return formule || "—";
  }
};

const getMaxCombats = (formule) => {
  switch (formule) {
    case "poule_2":
      return 2;
    case "poule_3":
      return 3;
    case "poule_4":
      return 6;
    case "poule_5":
      return 10;
    case "poule_6":
      return 12;
    default:
      return 0; // tableaux : inconnu → on affiche seulement "X combats"
  }
};

const progressBarClasses = (percent) => {
  if (percent >= 100) return "bg-slate-400 dark:bg-slate-600";
  if (percent >= 66) return "bg-emerald-500";
  if (percent >= 33) return "bg-amber-500";
  return "bg-red-500";
};

const distBadgeClassByRatio = (remaining, max) => {
  if (max <= 0)
    return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200";
  const ratio = remaining / max;
  if (remaining === 0)
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  if (ratio > 0.66)
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200";
  if (ratio > 0.33)
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200";
  return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200";
};

const matStripClasses = (isActive) =>
  isActive
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200"
    : "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300";

/* ---------------------------------------------
   Page
--------------------------------------------- */

const RepartitionCompetiteursTapisPage = () => {
  useScrollToTop();
  const { competition_id, tapis_id } = useParams();

  // ✅ roomKey unique pour ce tapis (utilisé par pause + prochain combat)
  const roomKey = useMemo(
    () => `${competition_id}_${tapis_id}`,
    [competition_id, tapis_id],
  );

  const [competition, setCompetition] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [selectedDistribution, setSelectedDistribution] = useState(null);
  const [combatsByDistribution, setCombatsByDistribution] = useState({});
  const [countByDist, setCountByDist] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);

  const [isPaused, setIsPaused] = useState(false);

  const distributionLocalNumberMap = useMemo(() => {
    const sorted = [...(distributions || [])].sort((a, b) => {
      const ai = Number(a.id);
      const bi = Number(b.id);

      if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;

      return String(a.id).localeCompare(String(b.id), "fr", {
        numeric: true,
        sensitivity: "base",
      });
    });

    const map = new Map();

    sorted.forEach((d, index) => {
      const key = Number(d.id) || d.id;
      map.set(key, index + 1);
    });

    return map;
  }, [distributions]);

  const getLocalDistNumber = (distId) => {
    const key = Number(distId) || distId;
    return distributionLocalNumberMap.get(key) ?? "—";
  };

  // ===============================
  // 🆕 Prochain combat (public)
  // ===============================
  const [publicNextFight, setPublicNextFight] = useState(null);
  const [publicNextMsg, setPublicNextMsg] = useState("");

  const [nextDistId, setNextDistId] = useState("");
  const [nextC1Id, setNextC1Id] = useState("");
  const [nextC2Id, setNextC2Id] = useState("");
  const [nextMessage, setNextMessage] = useState("");

  // -- Helpers tags
  const Chip = ({ children, className = "" }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  );

  const normalizeDisciplineKey = (value = "") => {
    const v = String(value).trim().toLowerCase();

    if (
      [
        "jujitsu combat",
        "ju-jitsu combat",
        "jj combat",
        "jujitsu",
        "ju-jitsu",
        "jujitsu fighting",
        "ju-jitsu fighting",
      ].includes(v)
    ) {
      return "jj_combat";
    }

    if (["jujitsu duo", "ju-jitsu duo", "jj duo", "duo system"].includes(v)) {
      return "jj_duo";
    }

    if (["ne-waza", "newaza", "ne waza", "sol grappling"].includes(v)) {
      return "ne-waza";
    }

    return "judo";
  };

  const selectedDiscipline = useMemo(() => {
    return (
      selectedDistribution?.discipline ||
      selectedDistribution?.Competiteurs?.[0]?.discipline_competiteur ||
      competition?.discipline ||
      distributions?.[0]?.discipline ||
      distributions?.[0]?.Competiteurs?.[0]?.discipline_competiteur ||
      "Judo"
    );
  }, [selectedDistribution, competition, distributions]);

  const publicDisciplineKey = useMemo(() => {
    return normalizeDisciplineKey(selectedDiscipline);
  }, [selectedDiscipline]);

  const publicUrl = useMemo(() => {
    if (publicDisciplineKey === "jj_combat") {
      return `/scoreboard-jjcombat/${competition_id}/${tapis_id}`;
    }

    if (publicDisciplineKey === "ne-waza") {
      return `/scoreboard-newaza/${competition_id}/${tapis_id}`;
    }

    if (publicDisciplineKey === "jj_duo") {
      return `/scoreboard-duo/${competition_id}/${tapis_id}`;
    }

    return `/score-public/${competition_id}/${tapis_id}`;
  }, [publicDisciplineKey, competition_id, tapis_id]);

  const GenderTag = ({ g }) => {
    if (g === "M") {
      return (
        <Chip className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
          Masculin
        </Chip>
      );
    }
    if (g === "F") {
      return (
        <Chip className="bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-200">
          Féminin
        </Chip>
      );
    }
    return (
      <Chip className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        {g || "—"}
      </Chip>
    );
  };

  // Initial load
  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        const c = await getCompetitionById(competition_id);
        setCompetition(c || null);
        await fetchDistributions();
      } catch {
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };
    boot();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition_id, tapis_id]);

  // Auto refresh
  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => {
        refreshNow();
      }, 15000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const fetchDistributions = async () => {
    try {
      const {
        distributions: dists,
        countDistributedCompetiteursByDistribution: countData,
      } = await getCompetitionTapisDistributions(competition_id, tapis_id);

      const active = (dists || []).filter((d) => !d.is_completed);
      setDistributions(active);
      setCountByDist(countData || []);
      await fetchCombatsCounts(active);

      // si la distribution sélectionnée n'est plus active, on la nettoie
      if (
        selectedDistribution &&
        !active.some((d) => d.id === selectedDistribution.id)
      ) {
        setSelectedDistribution(null);
      }

      // si la distribution "next" n'est plus active -> reset
      if (
        nextDistId &&
        !active.some((d) => String(d.id) === String(nextDistId))
      ) {
        setNextDistId("");
        setNextC1Id("");
        setNextC2Id("");
      }
    } catch {
      toast.error("Erreur lors du chargement des distributions du tapis.");
    }
  };

  // ✅ Abonnements socket (pause + prochain combat)
  useEffect(() => {
    socket.emit("join_combat", roomKey);

    const onPause = ({ paused }) => setIsPaused(!!paused);

    // ✅ NOUVEAU : ce qui est affiché au public
    const onNextFight = ({ nextFight, message }) => {
      setPublicNextFight(nextFight || null);
      setPublicNextMsg((message || "").toString());
    };

    socket.on("public_pause", onPause);
    socket.on("public_next_fight", onNextFight);

    return () => {
      socket.off("public_pause", onPause);
      socket.off("public_next_fight", onNextFight);
    };
  }, [roomKey]);

  // Emetteur pause/reprise
  const setPublicPause = (paused) => {
    socket.emit("public_pause", { roomKey, paused });
    setIsPaused(paused);
  };

  const fetchCombatsCounts = async (dists) => {
    const counts = {};
    for (const dist of dists) {
      try {
        const { count } = await countDistributionCombats(dist.id);
        counts[dist.id] = count;
      } catch {
        counts[dist.id] = 0;
      }
    }
    setCombatsByDistribution(counts);
  };

  const refreshNow = async () => {
    try {
      setRefreshing(true);
      await fetchDistributions();
    } finally {
      setRefreshing(false);
    }
  };

  const competitorCount = (distributionId) => {
    const row = countByDist.find((d) => d.id === distributionId);
    return row ? row.count : 0;
  };

  // ===============================
  // 🆕 Helpers prochain combat
  // ===============================
  const nextDist = useMemo(() => {
    const id = Number(nextDistId);
    return distributions.find((d) => Number(d.id) === id) || null;
  }, [distributions, nextDistId]);

  const nextCompetiteurs = useMemo(() => {
    return Array.isArray(nextDist?.Competiteurs) ? nextDist.Competiteurs : [];
  }, [nextDist]);

  const c1 = useMemo(() => {
    return (
      nextCompetiteurs.find((x) => String(x.id) === String(nextC1Id)) || null
    );
  }, [nextCompetiteurs, nextC1Id]);

  const c2 = useMemo(() => {
    return (
      nextCompetiteurs.find((x) => String(x.id) === String(nextC2Id)) || null
    );
  }, [nextCompetiteurs, nextC2Id]);

  const sendNextFightToPublic = () => {
    if (!nextDist) return toast.error("Choisis une distribution.");
    if (!c1 || !c2) return toast.error("Choisis 2 combattants.");
    if (String(c1.id) === String(c2.id))
      return toast.error("Les 2 combattants doivent être différents.");

    const payload = {
      competition_id: Number(competition_id),
      tapis_id: Number(tapis_id),

      distribution_id: Number(nextDist.id),
      formule: nextDist.formule_repartition,
      genre: nextDist.genre,
      categorie_age: nextDist.categorie_age,
      categorie_poids: nextDist.categorie_poids,

      c1: {
        id: c1.id,
        nom: c1.nom_competiteur,
        prenom: c1.prenom_competiteur,
        club: c1.club_competiteur,
      },
      c2: {
        id: c2.id,
        nom: c2.nom_competiteur,
        prenom: c2.prenom_competiteur,
        club: c2.club_competiteur,
      },

      ts: Date.now(),
    };

    // ✅ ICI : envoi socket au public
    socket.emit("public_next_fight", {
      roomKey,
      nextFight: payload,
      message: nextMessage,
    });

    toast.success("✅ Prochain combat envoyé à l’affichage public.");
  };

  const clearNextFightOnPublic = () => {
    socket.emit("public_next_fight", { roomKey, nextFight: null, message: "" });
    toast.info("Affichage du prochain combat effacé.");
  };

  // Agrégation tapis
  const matAgg = useMemo(() => {
    let maxTotal = 0; // somme max poules
    let doneTotal = 0; // faits poules
    let tableauDoneTotal = 0; // faits tableaux

    const enriched = distributions.map((dist) => {
      const done = combatsByDistribution[dist.id] ?? 0;
      const max = getMaxCombats(dist.formule_repartition);
      if (max > 0) {
        maxTotal += max;
        doneTotal += Math.min(done, max);
      } else {
        tableauDoneTotal += done;
      }
      return {
        ...dist,
        _done: done,
        _max: max,
        _remaining: Math.max(0, max - done),
        _isPoule: isPoule(dist.formule_repartition),
        _isBracket: isBracket(dist.formule_repartition),
        _competitorsCount: competitorCount(dist.id),
      };
    });

    const remainingTotal = Math.max(0, maxTotal - doneTotal);
    const percent = maxTotal > 0 ? Math.round((doneTotal / maxTotal) * 100) : 0;
    const isActive =
      (maxTotal > 0 && remainingTotal > 0) || tableauDoneTotal > 0;

    return {
      enriched,
      maxTotal,
      doneTotal,
      remainingTotal,
      percent,
      isActive,
      tableauDoneTotal,
    };
  }, [distributions, combatsByDistribution, countByDist]);

  if (loading || !competition) {
    return (
      <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
        <ScrollIndicator />
        <div className="max-w-[1700px] mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 bg-gray-200 dark:bg-slate-800 rounded" />
            <div className="h-10 w-full bg-gray-200 dark:bg-slate-800 rounded" />
            <div className="h-48 bg-gray-200 dark:bg-slate-800 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          ORGANISATEUR
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Tapis {tapis_id}
        </h1>
      </div>

      <div className="mb-4 max-w-[1700px] mx-auto w-full">
        <Breadcrumb competitionName={competition?.nom_competition} />
      </div>

      {/* Barre d’action “Affichage public” (avec ta capture, sans QR) */}
      <div className="max-w-[1700px] mx-auto w-full mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between p-4 rounded-2xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 shadow">
          <div className="flex items-center gap-4">
            {/* Aperçu visuel que tu avais déjà */}
            <div className="w-28 h-20 rounded-xl overflow-hidden ring-1 ring-gray-200 dark:ring-slate-700">
              <img
                src="/img/capture_score_01.jpg"
                alt="Affichage Public"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="min-w-0">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Tableau d’affichage public synchronisé
              </div>
              <div className="font-mono text-xs break-all text-gray-700 dark:text-gray-200">
                {window.location.origin}
                {publicUrl}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  title="Ouvrir l’affichage public dans un nouvel onglet"
                >
                  <FaExternalLinkAlt /> Ouvrir l’affichage
                </a>
                {/* Astuce d’utilisation en double écran */}
                <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">
                  Ouvrez, faites glisser sur l’écran HDMI et passez en plein
                  écran (F11).
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPublicPause(true)}
              disabled={isPaused}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
              title="Afficher 'Tapis en pause' sur l'écran public"
            >
              <FaPause /> Pause
            </button>
            <button
              onClick={() => setPublicPause(false)}
              disabled={!isPaused}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
              title="Reprendre l'affichage public"
            >
              <FaPlay /> Reprendre
            </button>
            <button
              onClick={refreshNow}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              title="Rafraîchir"
            >
              <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
              Rafraîchir
            </button>
            <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh 15s
            </label>
          </div>
        </div>
      </div>

      {/* 🆕 Prochain combat + message (affichage public) */}
      <div className="max-w-[1700px] mx-auto w-full mb-6">
        <div className="rounded-2xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 shadow p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-lg font-extrabold">
                Prochain combat (public)
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Choisis une distribution, 2 combattants, puis envoie au tableau
                public.
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={sendNextFightToPublic}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Envoyer
              </button>
              <button
                onClick={clearNextFightOnPublic}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white"
              >
                Effacer
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Distribution
              </label>
              <select
                value={nextDistId}
                onChange={(e) => {
                  setNextDistId(e.target.value);
                  setNextC1Id("");
                  setNextC2Id("");
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
              >
                <option value="">— Choisir —</option>
                {distributions.map((d) => (
                  <option key={d.id} value={d.id}>
                    #{getLocalDistNumber(d.id)} · {d.genre} · {d.categorie_age}{" "}
                    · {d.categorie_poids}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Combattant A
              </label>
              <select
                value={nextC1Id}
                onChange={(e) => setNextC1Id(e.target.value)}
                disabled={!nextDist}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="">— Choisir —</option>
                {nextCompetiteurs.map((x) => (
                  <option key={x.id} value={x.id}>
                    {String(x.nom_competiteur || "").toUpperCase()}{" "}
                    {x.prenom_competiteur} · {x.club_competiteur}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Combattant B
              </label>
              <select
                value={nextC2Id}
                onChange={(e) => setNextC2Id(e.target.value)}
                disabled={!nextDist}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="">— Choisir —</option>
                {nextCompetiteurs.map((x) => (
                  <option key={x.id} value={x.id}>
                    {String(x.nom_competiteur || "").toUpperCase()}{" "}
                    {x.prenom_competiteur} · {x.club_competiteur}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Message public
              </label>
              <input
                value={nextMessage}
                onChange={(e) => setNextMessage(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                placeholder="Ex: Préparez-vous : DUPONT vs MARTIN…"
              />
            </div>
          </div>

          {(publicNextFight || publicNextMsg) && (
            <div className="mt-4 text-sm rounded-xl border border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/20 p-3">
              <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                Actuellement affiché au public :
              </div>

              <div className="mt-1">
                {publicNextFight ? (
                  <>
                    <b>
                      #{getLocalDistNumber(publicNextFight.distribution_id)}
                    </b>{" "}
                    —{" "}
                    <b>
                      {String(publicNextFight.c1?.nom || "").toUpperCase()}{" "}
                      {publicNextFight.c1?.prenom}
                    </b>{" "}
                    vs{" "}
                    <b>
                      {String(publicNextFight.c2?.nom || "").toUpperCase()}{" "}
                      {publicNextFight.c2?.prenom}
                    </b>
                    {" · "}
                    {publicNextFight.genre} · {publicNextFight.categorie_age} ·{" "}
                    {publicNextFight.categorie_poids}
                  </>
                ) : (
                  <i>Aucun prochain combat défini.</i>
                )}
              </div>

              {!!publicNextMsg && (
                <div className="mt-1 text-xs">Message : {publicNextMsg}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Carte d’état du tapis */}
      <div className="max-w-[1700px] mx-auto w-full mb-6">
        <div className="rounded-2xl overflow-hidden border border-gray-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 shadow">
          <div className="flex">
            <div
              className={`w-[120px] ${matStripClasses(
                matAgg.isActive,
              )} flex flex-col items-center justify-center p-4`}
            >
              <p className="text-xs opacity-75">Tapis</p>
              <p className="text-6xl font-extrabold leading-none">{tapis_id}</p>
              <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold">
                {matAgg.isActive ? (
                  <>
                    <FaClock /> Actif
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> Terminé
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  {matAgg.maxTotal > 0 ? (
                    <>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        Combats restants (poules agrégées)
                      </div>
                      <div className="text-2xl font-bold">
                        {matAgg.remainingTotal}
                        <span className="text-sm font-normal text-gray-500">
                          {" "}
                          / {matAgg.maxTotal}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        Combats effectués (tableaux)
                      </div>
                      <div className="text-2xl font-bold">
                        {matAgg.tableauDoneTotal}
                      </div>
                    </>
                  )}
                </div>
                {matAgg.maxTotal > 0 && (
                  <div className="w-full sm:w-1/2">
                    <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
                      <div
                        className={`h-2 ${progressBarClasses(matAgg.percent)}`}
                        style={{ width: `${matAgg.percent}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Progression : {matAgg.percent}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grille des distributions */}
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {matAgg.enriched.map((dist) => {
                const max = dist._max;
                const done = dist._done;
                const remaining = dist._remaining;
                const hasProgress = max > 0;
                const percent = hasProgress
                  ? Math.round((Math.min(done, max) / max) * 100)
                  : 0;
                const badgeCls = dist._isBracket
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200"
                  : distBadgeClassByRatio(remaining, max);

                return (
                  <button
                    key={dist.id}
                    onClick={() => setSelectedDistribution(dist)}
                    className="text-left rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 hover:shadow-md transition p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          <span
                            className="text-gray-400 mr-2"
                            title={`ID technique : ${dist.id}`}
                          >
                            #{getLocalDistNumber(dist.id)}
                          </span>
                          {dist.genre} • {dist.categorie_age} •{" "}
                          {dist.categorie_poids}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          {formatFormule(dist.formule_repartition)} {" · "}{" "}
                          {dist._competitorsCount} compétiteur(s)
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badgeCls}`}
                      >
                        {dist._isBracket ? (
                          <>
                            {done} combat{done > 1 ? "s" : ""}
                          </>
                        ) : (
                          <>
                            {done}/{max}
                          </>
                        )}
                      </span>
                    </div>

                    {/* progress poule uniquement */}
                    {hasProgress && (
                      <div className="mt-2">
                        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
                          <div
                            className={`h-2 ${progressBarClasses(percent)}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          Progression : {percent}%
                        </div>
                      </div>
                    )}

                    {/* alerte derniers combats (poules) */}
                    {!dist._isBracket &&
                      max > 0 &&
                      max - done <= 2 &&
                      done !== max && (
                        <div className="mt-2 text-[11px] text-amber-800 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-1 rounded text-center">
                          ⚠️ Derniers combats à effectuer
                        </div>
                      )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Zone détail : composant de la distribution sélectionnée */}
      {selectedDistribution && (
        <div className="max-w-[1700px] mx-auto w-full">
          {/* Titre distribution sélectionnée */}
          <div className="mb-3 text-sm text-gray-700 dark:text-gray-200 flex flex-wrap items-center gap-2">
            <span className="text-gray-500 dark:text-gray-400">
              Distribution sélectionnée
            </span>
            <span
              className="font-bold text-gray-900 dark:text-white"
              title={`ID technique : ${selectedDistribution.id}`}
            >
              #{getLocalDistNumber(selectedDistribution.id)}
            </span>
            <span className="text-gray-400">•</span>
            <span className="font-medium">
              {formatFormule(selectedDistribution.formule_repartition)}
            </span>

            <GenderTag g={selectedDistribution.genre} />

            <Chip className="bg-black text-white dark:bg-black/90 dark:text-white">
              {selectedDistribution.categorie_age}
            </Chip>

            <Chip className="bg-black text-white dark:bg-black/90 dark:text-white">
              {selectedDistribution.categorie_poids}
            </Chip>
          </div>

          {selectedDistribution.formule_repartition === "poule_2" && (
            <div className="mt-2">
              <RepartitionPouleDeux
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                genre={selectedDistribution.genre}
                categorie_age={selectedDistribution.categorie_age}
                categorie_poids={selectedDistribution.categorie_poids}
                discipline={selectedDiscipline}
              />
            </div>
          )}
          {selectedDistribution.formule_repartition === "poule_3" && (
            <div className="mt-2">
              <RepartitionPouleTrois
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                genre={selectedDistribution.genre}
                categorie_age={selectedDistribution.categorie_age}
                categorie_poids={selectedDistribution.categorie_poids}
                discipline={selectedDiscipline}
              />
            </div>
          )}
          {selectedDistribution.formule_repartition === "poule_4" && (
            <div className="mt-2">
              <RepartitionPouleQuatre
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                genre={selectedDistribution.genre}
                categorie_age={selectedDistribution.categorie_age}
                categorie_poids={selectedDistribution.categorie_poids}
                discipline={selectedDiscipline}
              />
            </div>
          )}
          {selectedDistribution.formule_repartition === "poule_5" && (
            <div className="mt-2">
              <RepartitionPouleCinq
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                genre={selectedDistribution.genre}
                categorie_age={selectedDistribution.categorie_age}
                categorie_poids={selectedDistribution.categorie_poids}
                discipline={selectedDiscipline}
              />
            </div>
          )}
          {selectedDistribution.formule_repartition === "poule_6" && (
            <div className="mt-2">
              <RepartitionPouleSix
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                genre={selectedDistribution.genre}
                categorie_age={selectedDistribution.categorie_age}
                categorie_poids={selectedDistribution.categorie_poids}
                discipline={selectedDiscipline}
              />
            </div>
          )}

          {(selectedDistribution.formule_repartition === "tableau_base_8" ||
            selectedDistribution.formule_repartition === "tableau_base_16" ||
            selectedDistribution.formule_repartition === "tableau_base_32" ||
            selectedDistribution.formule_repartition === "tableau_base_64") && (
            <div className="mt-2">
              <RepartitionTableauSansRepechage
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                discipline={selectedDiscipline}
              />
            </div>
          )}

          {(selectedDistribution.formule_repartition ===
            "tableau_repechage_base_8" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_16" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_32" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_64") && (
            <div className="mt-2">
              <RepartitionTableauSystematiqueRepechage
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                discipline={selectedDiscipline}
              />
            </div>
          )}

          {/* {(selectedDistribution.formule_repartition ===
            "tableau_repechage_base_8" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_16" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_32" ||
            selectedDistribution.formule_repartition ===
              "tableau_repechage_base_64") && (
            <div className="mt-2">
              <RepartitionTableauSimpleRepechage
                competiteurs={selectedDistribution.Competiteurs}
                distributionId={selectedDistribution.id}
                distributionCompetiteurs={selectedDistribution.competiteurs}
                refreshDistribution={fetchDistributions}
                competitionId={competition_id}
                tapisId={tapis_id}
                discipline={selectedDiscipline}
              />
            </div>
          )} */}
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default RepartitionCompetiteursTapisPage;
